import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useLocation, useParams } from 'wouter';
import { fmtDateTime } from '@/lib/dateFormat';
import {
  Play, Pencil, ArrowLeft, CheckCircle, XCircle, Loader2,
  Zap, Clock, BarChart3, Eye, ChevronLeft, ChevronRight,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface CustomerAutomation {
  id: string;
  name: string;
  description: string | null;
  storeId: string;
  conditions: any[];
  actions: any[];
  lastRunAt: string | null;
  lastRunStatus: string | null;
}

interface RunRecord {
  id: string;
  runAt: string;
  status: string;
  totalScanned: number;
  totalMatched: number;
  totalActioned: number;
  errors: string[];
}

interface CustomerEvent {
  customerId: number;
  customerName?: string;
  customerEmail: string;
  results: { action: string; success: boolean; error?: string }[];
  success: boolean;
}

interface DoneEvent {
  totalScanned: number;
  totalMatched: number;
  totalActioned: number;
  errorCount: number;
  message: string;
}

interface PreviewCustomer {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  tags: string;
  createdAt: string;
}

interface PreviewResult {
  totalScanned: number;
  totalMatched: number;
  page: number;
  perPage: number;
  totalPages: number;
  customers: PreviewCustomer[];
}

export function CustomerAutomationDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { token } = useAuthStore();
  const { toast } = useToast();

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const apiFetch = async (path: string, opts: RequestInit = {}) => {
    const r = await fetch(`${BASE}/api${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...authHeader, ...(opts.headers as any) },
    });
    if (!r.ok && r.status !== 204) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error ?? r.statusText);
    }
    if (r.status === 204) return null;
    return r.json();
  };

  const [automation, setAutomation] = useState<CustomerAutomation | null>(null);
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Preview state
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState(1);

  // Run state
  const [running, setRunning] = useState(false);
  const [runPhase, setRunPhase] = useState<'idle' | 'scanning' | 'acting' | 'done' | 'error'>('idle');
  const [scanProgress, setScanProgress] = useState({ scanned: 0, matched: 0 });
  const [customerEvents, setCustomerEvents] = useState<CustomerEvent[]>([]);
  const [doneResult, setDoneResult] = useState<DoneEvent | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  const loadData = async () => {
    try {
      const [auto, runHistory] = await Promise.all([
        apiFetch(`/customer-automations/${params.id}`),
        apiFetch(`/customer-automations/${params.id}/runs`),
      ]);
      setAutomation(auto);
      setRuns(runHistory);
    } catch (err: any) {
      toast({ title: 'Error loading automation', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [customerEvents]);

  const fetchPreviewPage = async (page: number) => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const result = await apiFetch(`/customer-automations/${params.id}/preview?page=${page}&perPage=25`, { method: 'POST' });
      setPreviewResult(result);
      setPreviewPage(result.page);
    } catch (err: any) {
      setPreviewError(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreview = () => {
    setPreviewPage(1);
    fetchPreviewPage(1);
  };

  const handlePreviewPage = (p: number) => {
    fetchPreviewPage(p);
  };

  const handleRun = async () => {
    setRunning(true);
    setRunPhase('scanning');
    setScanProgress({ scanned: 0, matched: 0 });
    setCustomerEvents([]);
    setDoneResult(null);
    setRunError(null);

    const abortedRef = { current: false };

    try {
      const response = await fetch(`${BASE}/api/customer-automations/${params.id}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? response.statusText);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (abortedRef.current) break;
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          if (!part.trim() || part.startsWith(':')) continue;
          const lines = part.split('\n');
          let eventType = 'message';
          let dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr = line.slice(6).trim();
          }
          if (!dataStr) continue;
          try {
            const data = JSON.parse(dataStr);
            if (eventType === 'start') {
              setRunPhase('scanning');
            } else if (eventType === 'progress') {
              setScanProgress({ scanned: data.scanned, matched: data.matched });
            } else if (eventType === 'scan_done') {
              setRunPhase('acting');
              setScanProgress({ scanned: data.totalScanned, matched: data.totalMatched });
            } else if (eventType === 'customer') {
              setCustomerEvents((prev) => [...prev, data as CustomerEvent]);
            } else if (eventType === 'done') {
              setDoneResult(data as DoneEvent);
              setRunPhase('done');
              loadData();
            } else if (eventType === 'error') {
              setRunError(data.message);
              setRunPhase('error');
            }
          } catch {}
        }
      }
    } catch (err: any) {
      setRunError(err.message);
      setRunPhase('error');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!automation) {
    return (
      <Layout>
        <div className="text-center py-20">
          <p className="text-muted-foreground">Automation not found.</p>
          <Button className="mt-4" onClick={() => setLocation('/customer-automations')}>Back to list</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <button
            onClick={() => setLocation('/customer-automations')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Customer Automations
          </button>
          <h1 className="text-3xl font-display text-gradient mb-1">{automation.name}</h1>
          {automation.description && <p className="text-muted-foreground">{automation.description}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" className="gap-2" onClick={() => setLocation(`/customer-automations/${params.id}/edit`)}>
            <Pencil className="w-4 h-4" /> Edit
          </Button>
          <Button variant="secondary" className="gap-2" onClick={handlePreview} isLoading={previewing} disabled={running}>
            <Eye className="w-4 h-4" /> Preview
          </Button>
          <Button className="gap-2" onClick={handleRun} disabled={running} isLoading={running}>
            <Play className="w-4 h-4" /> {running ? 'Running...' : 'Run Now'}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="glass-panel rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Conditions</p>
          <p className="text-2xl font-display font-bold text-primary">{automation.conditions.length}</p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Actions</p>
          <p className="text-2xl font-display font-bold">{automation.actions.length}</p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Last run</p>
          <p className="text-sm font-medium">
            {fmtDateTime(automation.lastRunAt)}
          </p>
        </div>
        <div className="glass-panel rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Last status</p>
          {automation.lastRunStatus === 'success' ? (
            <span className="flex items-center gap-1 text-sm text-emerald-400"><CheckCircle className="w-4 h-4" /> Success</span>
          ) : automation.lastRunStatus === 'error' ? (
            <span className="flex items-center gap-1 text-sm text-rose-400"><XCircle className="w-4 h-4" /> Error</span>
          ) : (
            <span className="text-sm text-muted-foreground">Never run</span>
          )}
        </div>
      </div>

      {/* Preview section */}
      {(previewing || previewResult || previewError) && (
        <div className="glass-panel rounded-2xl p-6 border border-border mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold flex items-center gap-2 text-foreground">
              <Eye className="w-5 h-5 text-primary" /> Customer Preview (Dry Run)
            </h2>
            {previewResult && (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>
                  <span className="text-primary font-bold">{previewResult.totalMatched}</span> matching of{' '}
                  <span className="font-bold">{previewResult.totalScanned}</span> scanned
                </span>
              </div>
            )}
          </div>

          {previewing && (
            <div className="flex items-center gap-3 py-8 justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              Scanning customers (this may take a moment)...
            </div>
          )}

          {previewError && (
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-sm text-rose-400">
              {previewError}
            </div>
          )}

          {previewResult && !previewing && (
            <>
              {previewResult.customers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No customers match the current filter conditions.</p>
              ) : (
                <div className="overflow-auto rounded-xl border border-border/50">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/50 bg-muted/20">
                        <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Name</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Phone</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Current Tags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewResult.customers.map((c) => (
                        <tr key={c.id} className="border-b border-border/50 hover:bg-muted/50">
                          <td className="px-4 py-3 text-foreground">{c.name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{c.email}</td>
                          <td className="px-4 py-3 text-muted-foreground">{c.phone ?? '—'}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs max-w-48 truncate">{c.tags || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {previewResult.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                  <p className="text-xs text-muted-foreground">
                    Page {previewResult.page} of {previewResult.totalPages} ({previewResult.totalMatched} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={previewResult.page <= 1 || previewing}
                      onClick={() => handlePreviewPage(previewResult.page - 1)}
                      className="p-2 rounded-xl bg-muted/50 border border-border disabled:opacity-40 hover:bg-muted transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-muted-foreground min-w-12 text-center">
                      {previewResult.page} / {previewResult.totalPages}
                    </span>
                    <button
                      disabled={previewResult.page >= previewResult.totalPages || previewing}
                      onClick={() => handlePreviewPage(previewResult.page + 1)}
                      className="p-2 rounded-xl bg-muted/50 border border-border disabled:opacity-40 hover:bg-muted transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Live run progress */}
      {(running || runPhase !== 'idle') && (
        <div className="glass-panel rounded-2xl p-6 border border-primary/20 mb-6">
          <h2 className="text-base font-bold flex items-center gap-2 mb-4 text-foreground">
            {running ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : <Zap className="w-5 h-5 text-primary" />}
            {runPhase === 'scanning' && 'Scanning customers...'}
            {runPhase === 'acting' && 'Executing actions...'}
            {runPhase === 'done' && 'Run complete'}
            {runPhase === 'error' && 'Run failed'}
          </h2>

          {(runPhase === 'scanning' || runPhase === 'acting') && (
            <div className="flex gap-6 mb-4">
              <div className="text-center">
                <p className="text-2xl font-display font-bold text-primary">{scanProgress.scanned}</p>
                <p className="text-xs text-muted-foreground">Scanned</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-display font-bold">{scanProgress.matched}</p>
                <p className="text-xs text-muted-foreground">Matched</p>
              </div>
            </div>
          )}

          {doneResult && runPhase === 'done' && (
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center bg-muted/50 rounded-xl p-3">
                <p className="text-2xl font-display font-bold text-primary">{doneResult.totalActioned}</p>
                <p className="text-xs text-muted-foreground">Actioned</p>
              </div>
              <div className="text-center bg-muted/50 rounded-xl p-3">
                <p className="text-2xl font-display font-bold">{doneResult.totalMatched}</p>
                <p className="text-xs text-muted-foreground">Matched</p>
              </div>
              <div className="text-center bg-muted/50 rounded-xl p-3">
                <p className={`text-2xl font-display font-bold ${doneResult.errorCount > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {doneResult.errorCount}
                </p>
                <p className="text-xs text-muted-foreground">Errors</p>
              </div>
            </div>
          )}

          {runError && (
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-sm text-rose-400 mb-4">
              {runError}
            </div>
          )}

          {customerEvents.length > 0 && (
            <div className="max-h-80 overflow-y-auto space-y-1 bg-muted/50 rounded-xl p-3 border border-border/50">
              {customerEvents.map((evt, i) => (
                <div key={i} className="flex items-start gap-2 py-1 border-b border-border/50 last:border-0">
                  {evt.success ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 text-xs">
                    <span className="text-foreground">{evt.customerName || evt.customerEmail}</span>
                    <span className="text-muted-foreground ml-2">{evt.customerEmail}</span>
                    <div className="flex flex-wrap gap-2 mt-0.5">
                      {evt.results.map((r, ri) => (
                        <span key={ri} className={`${r.success ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {r.action} {r.success ? '✓' : `✗ ${r.error ?? ''}`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={eventsEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Run history */}
      <div className="glass-panel rounded-2xl p-6 border border-border">
        <h2 className="text-base font-bold flex items-center gap-2 mb-4 text-foreground">
          <BarChart3 className="w-5 h-5 text-primary" /> Run History
        </h2>
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No runs yet. Click "Run Now" to execute this automation.</p>
        ) : (
          <div className="overflow-auto rounded-xl border border-border/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 bg-muted/20">
                  <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Run at</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Scanned</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Matched</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Actioned</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Errors</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="px-4 py-3 text-foreground">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                        {fmtDateTime(run.runAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {run.status === 'done' ? (
                        <span className="flex items-center gap-1 text-emerald-400"><CheckCircle className="w-3.5 h-3.5" /> Done</span>
                      ) : run.status === 'error' ? (
                        <span className="flex items-center gap-1 text-rose-400"><XCircle className="w-3.5 h-3.5" /> Error</span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {run.status}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">{run.totalScanned}</td>
                    <td className="px-4 py-3 text-right">{run.totalMatched}</td>
                    <td className="px-4 py-3 text-right text-emerald-400">{run.totalActioned}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={run.errors.length > 0 ? 'text-rose-400' : 'text-muted-foreground'}>
                        {run.errors.length}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
